import React, { useEffect, useState } from 'react'
import { Button, Breadcrumb, Modal, Drawer, Form, Col, Input } from 'antd'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import SelectInput from '../../components/Forms/SelectInput'
import FilterTable from '../../components/Forms/searchFilter'
import PageLoader from '../../components/Layouts/PageLoader'
import WithData from 'hocs/WithData'
import { columns } from './tableColumns'
import CarTaskForm from './Form'
import CarTaskUpdate from './Update'
import {
  carTasksUrl,
  deleteRequest,
  errorNotification,
  formatUrl,
  getRequest,
  postRequest,
  successNotification
} from '../../config'
import { CAR_VENDORS, CAR_TASK_STAGES } from 'app-constants'
import PageTitle from 'components/PageTitle'

const { confirm } = Modal

const CarCreationTasks = () => {
  const [tasks, setTasks] = useState([])
  const [loading, setLoading] = useState(false)
  const [meta, setMeta] = useState({
    previous: null,
    next: null,
    count: 0
  })
  const [modalKey, setModalKey] = useState(1)
  const [showCarTask, setShowCarTask] = useState(false)
  const [showUpdateModal, setShowUpdateModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [currentTask, setCurrentTask] = useState({})
  const [updateModalKey, setUpdateModalKey] = useState(0)
  const [showFilters, setShowFilters] = useState(false)
  const [searchPayload, setSearchPayload] = useState({
    type: '',
    completed: null, // Allow null for "All"
    retries: '',
    stage: ''
  })
  const [selectedRowKeys, setSelectedRowKeys] = useState([])

  const onSelectChange = newSelectedRowKeys => {
    setSelectedRowKeys(newSelectedRowKeys)
  }

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange
  }

  // Handlers for filter inputs
  const handleSelect = stateName => value => {
    setSearchPayload(prev => ({ ...prev, [stateName]: value }))
  }

  const handleInputChange = stateName => event => {
    setSearchPayload(prev => ({ ...prev, [stateName]: event.target.value }))
  }

  const handleDeleteTask = async () => {
    const response = await deleteRequest(
      `${carTasksUrl}/${currentTask.id}`,
      'car task',
      setLoading
    )
    if (response.success) {
      successNotification('Car Task Deleted')
      setShowDeleteModal(false)
      searchTasks()
    } else {
      if (response.error.response) {
        errorNotification(response.error.response.data.message)
      }
    }
  }

  const handleBulkDelete = async () => {
    confirm({
      title: `Are you sure you want to delete ${selectedRowKeys.length} tasks?`,
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone.',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        const response = await postRequest(
          `${carTasksUrl}/delete-multiple/`,
          { ids: selectedRowKeys },
          'bulk delete tasks',
          setLoading
        )
        if (response.success) {
          successNotification('Tasks Deleted Successfully')
          setSelectedRowKeys([])
          searchTasks()
        } else {
          if (response.error.response) {
            errorNotification(response.error.response.data.message)
          }
        }
      },
      onCancel() {
        console.log('Cancel')
      }
    })
  }

  const searchTasks = async (customUrl = '', filters = {}) => {
    let url = customUrl ? formatUrl(customUrl) : carTasksUrl

    // Construct query params from filters if not a pagination URL (which already has them)
    // or if we are applying new filters.
    if (!customUrl) {
      const params = new URLSearchParams()
      if (filters.type) params.append('type', filters.type)
      if (
        filters.completed !== null &&
        filters.completed !== undefined &&
        filters.completed !== ''
      ) {
        params.append('completed', filters.completed)
      }
      if (filters.retries) params.append('retries', filters.retries)
      if (filters.stage) params.append('stage', filters.stage)

      const queryString = params.toString()
      if (queryString) {
        url += `?${queryString}`
      }
    }

    const response = await getRequest(url, 'car tasks', setLoading)
    if (response) {
      setTasks(response.data.results)
      setMeta({
        ...response.data,
        results: response.data.results.length
      })
    }
  }

  useEffect(() => {
    searchTasks()
  }, [])

  useEffect(() => {
    if (showCarTask) {
      setModalKey(modalKey + 1)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCarTask])

  useEffect(() => {
    if (showUpdateModal) {
      setUpdateModalKey(updateModalKey + 1)
    }
    // eslint-disable-next-line
  }, [showUpdateModal])

  return (
    <PageLoader className="container" loading={loading}>
      <PageTitle title="Car preparation tasks" />
      <div className="row">
        <div className="col-lg-12">
          <Breadcrumb>
            <Breadcrumb.Item href="/">
              {/* <HomeOutlined /> */}
              <span>Home</span>
            </Breadcrumb.Item>
            <Breadcrumb.Item>Car Tasks</Breadcrumb.Item>
          </Breadcrumb>
          <br />
          <br />
          <div className="card mb-4rem">
            <div className="card-header">
              <div className="utils__title">
                <span>Car Tasks {meta && `(${meta.count})`}</span>
              </div>
            </div>
            <div className="card-body">
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div
                  style={{
                    flex: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    marginBottom: '1rem',
                    width: '100%'
                  }}
                >
                  <Button
                    type="primary"
                    // eslint-disable-next-line react/prop-types
                    onClick={() => setShowCarTask(true)}
                    style={{ marginRight: '1rem' }}
                  >
                    Create Car Task
                  </Button>

                  <Button type="default" onClick={() => setShowFilters(true)}>
                    Apply Filters
                  </Button>

                  {selectedRowKeys.length > 0 && (
                    <Button
                      type="primary"
                      danger
                      onClick={handleBulkDelete}
                      style={{ marginLeft: '1rem' }}
                    >
                      Delete Selected ({selectedRowKeys.length})
                    </Button>
                  )}
                </div>
              </div>
              <Drawer
                title={'Apply Filters'}
                placement="right"
                open={showFilters}
                closable={true}
                onClose={() => setShowFilters(false)}
                width={400}
              >
                <div className="my-4">
                  <Form layout="vertical">
                    <Col md={24}>
                      <SelectInput
                        placeholder="Select Type"
                        label="Task Type"
                        name="type"
                        value={searchPayload.type}
                        onChange={handleSelect('type')}
                        optionList={CAR_VENDORS}
                        allowClear
                      />
                    </Col>

                    <Col md={24} className="mt-3">
                      <SelectInput
                        placeholder="Select Status"
                        label="Completed"
                        name="completed"
                        value={searchPayload.completed}
                        onChange={handleSelect('completed')}
                        optionList={[
                          { value: 'true', label: 'Yes' },
                          { value: 'false', label: 'No' }
                        ]}
                        allowClear
                      />
                    </Col>

                    <Col md={24} className="mt-3">
                      <Form.Item label="Retries">
                        <Input
                          type="number"
                          placeholder="Retries"
                          value={searchPayload.retries}
                          onChange={handleInputChange('retries')}
                        />
                      </Form.Item>
                    </Col>
                    <Col md={24} className="mt-3">
                      <SelectInput
                        placeholder="Select Stage"
                        label="Task Stage"
                        name="stage"
                        value={searchPayload.stage}
                        onChange={handleSelect('stage')}
                        optionList={CAR_TASK_STAGES}
                        allowClear
                      />
                    </Col>

                    <Col md={24} className="mt-4">
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'flex-end',
                          gap: '10px'
                        }}
                      >
                        <Button
                          onClick={() => {
                            setSearchPayload({
                              type: '',
                              completed: null,
                              retries: '',
                              stage: ''
                            })
                            searchTasks('', {
                              type: '',
                              completed: '',
                              retries: '',
                              stage: ''
                            })
                          }}
                        >
                          Reset
                        </Button>
                        <Button
                          type="primary"
                          onClick={() => {
                            searchTasks('', searchPayload)
                          }}
                          loading={loading}
                        >
                          Submit
                        </Button>
                      </div>
                    </Col>
                  </Form>
                </div>
              </Drawer>
              <FilterTable
                dataSource={tasks}
                columns={columns(
                  setCurrentTask,
                  setShowUpdateModal,
                  setShowDeleteModal
                )}
                meta={meta}
                getData={url => searchTasks(url)}
                rowSelection={rowSelection}
              />

              <Modal
                title="Create Car Task"
                centered
                open={showCarTask}
                onOk={() => setShowCarTask(false)}
                onCancel={() => setShowCarTask(false)}
                footer={null}
                width={700}
              >
                <CarTaskForm
                  key={modalKey}
                  handleCancel={() => {
                    setShowCarTask(false)
                    searchTasks()
                  }}
                />
              </Modal>

              <Modal
                title="Update Car Task"
                centered
                open={showUpdateModal}
                onOk={() => setShowUpdateModal(false)}
                onCancel={() => setShowUpdateModal(false)}
                footer={null}
                width={700}
              >
                <CarTaskUpdate
                  key={updateModalKey}
                  task={currentTask}
                  handleCancel={data => {
                    setShowUpdateModal(false)
                    setTasks(prev =>
                      prev.map(task => (task.id === data.id ? data : task))
                    )
                  }}
                />
              </Modal>

              <Modal
                title="Do You Really Want to Delete This Task?"
                centered
                open={showDeleteModal}
                onOk={() => setShowDeleteModal(false)}
                onCancel={() => setShowDeleteModal(false)}
                footer={null}
              >
                <div
                  className="p-3"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                >
                  <span className="text-danger mb-3">
                    Go ahead and delete task?
                  </span>
                  <Button
                    type="danger"
                    onClick={() => {
                      handleDeleteTask()
                    }}
                    loading={loading}
                  >
                    Delete Task
                  </Button>
                </div>
              </Modal>
            </div>
          </div>
        </div>
      </div>
    </PageLoader>
  )
}

export default WithData(CarCreationTasks)
